import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, IconButton, Chip, Fab, Avatar,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import { Add, Edit, DeleteOutline, RestaurantMenuRounded } from '@mui/icons-material';
import { PageHeader, ListRow, ConfirmDialog, useToast } from '../Components/ui';
import DishFormDialog from '../Components/DishFormDialog';
import { radius, FAB_BOTTOM_OFFSET } from '../theme';

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function Dishes() {
  const [tabIndex,        setTabIndex]        = useState(0); // 0 = eigene, 1 = community
  const [myDishes,        setMyDishes]        = useState([]);
  const [communityDishes, setCommunityDishes] = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [formOpen,        setFormOpen]        = useState(false);
  const [editingDish,     setEditingDish]     = useState(null);
  const [detailDish,      setDetailDish]      = useState(null);
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const { showToast } = useToast();

  const fetchMine = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch('/api/dishes/mine', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMyDishes(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchCommunity = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch('/api/dishes/community', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCommunityDishes(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchMine(), fetchCommunity()]).finally(() => setLoading(false));
  }, [fetchMine, fetchCommunity]);

  const openAdd  = () => { setEditingDish(null); setFormOpen(true); };
  const openEdit = (dish) => { setEditingDish(dish); setFormOpen(true); };

  const handleSaved = () => {
    setFormOpen(false);
    fetchMine();
    fetchCommunity();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/dishes/${deleteTarget}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMyDishes((prev) => prev.filter((d) => d.id !== deleteTarget));
        setCommunityDishes((prev) => prev.filter((d) => d.id !== deleteTarget));
      } else {
        showToast('Fehler beim Löschen.', { severity: 'error' });
      }
    } catch {
      showToast('Verbindungsfehler.', { severity: 'error' });
    }
    setDeleteTarget(null);
  };

  const list = tabIndex === 0 ? myDishes : communityDishes;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Container maxWidth="sm" sx={{ pt: 3, pb: 14, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        <PageHeader title="Gerichte" icon={<RestaurantMenuRounded fontSize="small" />} sx={{ mb: 0 }} />

        {/* Sub-Tabs */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['Eigene Gerichte', 'Community'].map((label, i) => (
            <Chip
              key={label}
              label={label}
              onClick={() => setTabIndex(i)}
              color={tabIndex === i ? 'primary' : 'default'}
              variant={tabIndex === i ? 'filled' : 'outlined'}
              sx={{ fontWeight: tabIndex === i ? 700 : 400 }}
            />
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : list.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            {tabIndex === 0 ? 'Noch keine eigenen Gerichte angelegt.' : 'Noch keine öffentlichen Gerichte vorhanden.'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {list.map((dish) => (
              <ListRow
                key={dish.id}
                onClick={() => setDetailDish(dish)}
                leading={
                  <Avatar variant="rounded" src={dish.photo_url || undefined} sx={{ bgcolor: 'action.hover' }}>
                    <RestaurantMenuRounded sx={{ color: 'text.disabled' }} />
                  </Avatar>
                }
                title={dish.name}
                subtitle={`${dish.components.length} ${dish.components.length === 1 ? 'Zutat' : 'Zutaten'}${dish.is_public ? ' · Community' : ''}`}
                trailing={tabIndex === 0 && (
                  <>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(dish); }} sx={{ color: 'text.secondary' }}>
                      <Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(dish.id); }} sx={{ color: 'text.disabled' }}>
                      <DeleteOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </>
                )}
              />
            ))}
          </Box>
        )}

      </Container>

      <Fab color="primary" onClick={openAdd} sx={{ position: 'fixed', bottom: FAB_BOTTOM_OFFSET, right: 20, zIndex: 10 }}>
        <Add />
      </Fab>

      <DishFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        dish={editingDish}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Gericht löschen?"
        message="Dieses Gericht wird endgültig gelöscht."
        confirmLabel="Löschen"
        destructive
      />

      {/* ── Detail-Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={Boolean(detailDish)} onClose={() => setDetailDish(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{detailDish?.name}</DialogTitle>
        <DialogContent>
          {detailDish?.photo_url && (
            <Box
              component="img"
              src={detailDish.photo_url}
              alt={detailDish.name}
              sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: radius.lg, mb: 2 }}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {detailDish?.components.map((c) => (
              <Box key={c.id} sx={{ border: 1, borderColor: 'divider', borderRadius: radius.lg, p: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.kcal_per_100g} kcal / 100g · P {c.protein_g}g · F {c.fat_g}g · C {c.carbs_g}g
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button fullWidth variant="contained" onClick={() => setDetailDish(null)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
